import { useEffect, useState } from 'react';

/**
 * Instalação do PETBEA no celular (PWA): prompt nativo no Android/Chrome/Edge, instrução no iPhone
 * (o Safari não tem prompt) e detecção de quando já está instalado (aberto em modo app).
 */

interface EventoInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// O evento pode disparar antes do componente montar (ex.: na home): guarda o último aqui.
let eventoGuardado: EventoInstalacao | null = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    eventoGuardado = e as EventoInstalacao;
  });
}

function emModoApp(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone === true;
}

function detectarIos(): boolean {
  const ua = window.navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
}

export function useInstalarApp() {
  const [evento, setEvento] = useState<EventoInstalacao | null>(eventoGuardado);
  const [instalado, setInstalado] = useState(emModoApp);
  const ios = detectarIos();

  useEffect(() => {
    const aoOferecer = (e: Event) => {
      e.preventDefault();
      eventoGuardado = e as EventoInstalacao;
      setEvento(eventoGuardado);
    };
    const aoInstalar = () => setInstalado(true);
    window.addEventListener('beforeinstallprompt', aoOferecer);
    window.addEventListener('appinstalled', aoInstalar);
    return () => {
      window.removeEventListener('beforeinstallprompt', aoOferecer);
      window.removeEventListener('appinstalled', aoInstalar);
    };
  }, []);

  /** Abre o prompt nativo. Devolve false quando não há prompt disponível neste navegador. */
  const instalar = async (): Promise<boolean> => {
    if (!evento) return false;
    await evento.prompt();
    const escolha = await evento.userChoice;
    if (escolha.outcome === 'accepted') setInstalado(true);
    eventoGuardado = null;
    setEvento(null);
    return true;
  };

  return { podeInstalar: Boolean(evento), ios, instalado, instalar };
}
